import * as React from "react";
import * as ReactDOM from "react-dom";
import { Route } from "react-router-dom";
import * as modelsGet from "../../../models/get";
import * as modelsPost from "../../../models/post";
import * as modelsForm from "../../../models/form";
import * as utils from "../../../utils/utils";
import * as apiUsers from "../../../api/users";
import * as apiUserGroups from "../../../api/userGroups";
import * as apiPersons from "../../../api/persons";
import * as apiSettings from "../../../api/settings";
import { Window } from "../../../components/window";
import { Loader, LoaderStyle } from "../../../components/loader";
import { Button, ButtonType } from "../../../components/button";
import { ScrollViewer, ScrollBarVisibility } from "../../../components/scrollViewer";
import { FormGroup } from "../../../components/form";
import { ComboBox, ComboBoxType, DROPDOWN_TEXT_ITEM_HEIGHT, DropDownTextItem } from "../../../components/comboBox";
import { InputMask, InputSize, InputSideButton } from "../../../components/input";
import { InputValueType } from "../../../enumerations";
import { TextBox } from "../../../components/textBox";
import { CheckBox } from "../../../components/checkbox";
import { Alert, AlertType } from "../../../components/alert";

enum Mode
{
    create,
    edit
}

interface IProperties
{
    /**
     * Die ID des zu öffnenden Datensatzes. (Ist leer wenn eine neuer Datensatz erstellt werden soll.)
     */
    userId?: number,

    /**
     * Gibt an, ob alle Lizenzen aufgebraucht sind.
     */
    isLicenceLimitReached: boolean,

    /**
     * Wird aufgerufen sobald sich das Fenster schließt.
     */
    onClose?: (refresh?: boolean) => void
}

interface IState
{
    /**
     * Gibt an, ob Daten gerade geladen werden.
     */
    isLoading?: boolean,

    /**
     * Gibt den Modus an in dem sich der Dialog befindet.
     */
    mode?: Mode,

    /**
     * Wenn im Bearbeitungsmodus, dann ist diese Eigenschaft gefüllt.
     */
    user?: modelsGet.IUser,

    /**
     * Enthält alle Benutzergruppen.
     */
    userGroups?: modelsGet.IUserGroup[],

    /**
     * Enthält alle Bestatter.
     */
    morticians?: modelsGet.IPerson[],

    /**
     * Enthält alle Zeitzonen.
     */
    timezones?: modelsGet.ITimezone[],

    /**
     * Enthält die Eingabedaten des Forms.
     */
    form?: {
        userGroup?: modelsForm.IComboBoxValue<string, number>,
        mortician?: modelsForm.IComboBoxValue<string, number>,
        email?: string,
        firstname?: string,
        lastname?: string,
        password?: string,
        passwordConfirmed?: string,
        timezone?: modelsForm.IComboBoxValue<string, string>,
        isDeactivated?: boolean
    },

    /**
     * Enthält Serverseitige Fehlermeldungen.
     */
    errors?: Object,

    /**
     * Der Text der Meldungen welche angezeigt werden soll.
     */
    alertMessage?: string,

    /**
     * Der Typ der Meldung.
     */
    alertType?: AlertType
}

export default class Dialog extends React.Component<IProperties, IState>
{
    private _initialState: IState;
    private _isSaveButtonDisabled: boolean;

    /**
     * Erstellt eine neue Instanz der Klasse @see UsersDialog.
     * @param props Die Eigenschaften dieser Komponente.
     */
    constructor(props: IProperties)
    {
        super(props);

        // Modus ermitteln.
        var mode: Mode = Mode.create;
        if (props.userId > 0)
        {
            mode = Mode.edit;
        }

        this.state = {
            isLoading: true,
            mode: mode,
            form: {
                userGroup: {},
                mortician: {},
                timezone: {}
            },
            errors: {}
        };

        this.initAsync(props);
    }

    /**
     * Lädt alle benötigten Daten und/oder weißt diese zu.
     * @param props Alle gesetzten Eigenschaften.
     */
    private async initAsync(props: IProperties)
    {
        if (this.state.mode === Mode.edit)
        {
            await this.initEditModeAsync(props);
        }
        else if (this.state.mode === Mode.create)
        {
            await this.initCreateModeAsync(props);
        }
    }

    /**
     * Initialisert den Edit Mode.
     * @param props Alle gesetzten Eigenschaften.
     */
    private async initEditModeAsync(props: IProperties)
    {
        var user: modelsGet.IUser = await apiUsers.getUser(props.userId);
        var userGroups: modelsGet.IUserGroup[] = await apiUserGroups.getUserGroups();
        var morticians: modelsGet.IPerson[] = await apiPersons.getMorticians(false);
        var timezones: modelsGet.ITimezone[] = await apiSettings.getTimezones();

        this.setState((state: IState) =>
        {
            state.isLoading = false;
            state.errors = {};
            state.alertMessage = null;
            state.alertType = null;
            state.user = user;
            state.userGroups = userGroups;
            state.morticians = morticians
            state.timezones = timezones;

            state.form.userGroup = {
                selectedValue: user.group_id,
                value: user.group_name
            };

            let mortician: modelsGet.IPerson = morticians.where(m => m.id === user.mortician_id).first();
            if (mortician != null)
            {
                state.form.mortician = {
                    selectedValue: mortician.id,
                    value: mortician.fullname_and_address_without_title_rev
                };
            }
            else
            {
                state.form.mortician = {};
            }

            state.form.email = user.email;
            state.form.firstname = user.firstname;
            state.form.lastname = user.lastname;

            let timezone: modelsGet.ITimezone = timezones.where(t => t.key === user.settings.timezone).first();
            state.form.timezone = {
                selectedValue: timezone.key,
                value: timezone.text
            };
            state.form.isDeactivated = user.is_deactivated;

            return state;
        }, () => this._initialState = {
            ...this.state,
            form: {
                ...this.state.form
            }
        });
    }

    /**
     * Initialisert den Create Mode.
     * @param props Alle gesetzten Eigenschaften.
     */
    private async initCreateModeAsync(props: IProperties)
    {
        var userGroups: modelsGet.IUserGroup[] = await apiUserGroups.getUserGroups();
        var morticians: modelsGet.IPerson[] = await apiPersons.getMorticians(false);
        var timezones: modelsGet.ITimezone[] = await apiSettings.getTimezones();

        // Neues State Objekt erstellen.
        this.setState(() =>
        {
            const newState: IState = {};
            newState.isLoading = false;
            newState.errors = {};
            newState.alertMessage = null;
            newState.alertType = null;
            newState.userGroups = userGroups;
            newState.morticians = morticians
            newState.timezones = timezones;
            let timezone: modelsGet.ITimezone = timezones.where(t => t.key === "W. Europe Standard Time").first();

            newState.form = {
                userGroup: {},
                mortician: {},
                timezone: {
                    selectedValue: timezone.key,
                    value: timezone.text
                }
            };

            return newState;
        }, () => this._initialState = {
            ...this.state,
            form: {
                ...this.state.form
            }
        });
    }

    /**
     * Wird ausgeführt, bevor gerendert wird.
     * Hier wird geprüft ob der Button zum erstellen oder speichern
     * freigschaltet werden soll.
     * @param nextProps Die neuen Eigenschaften.
     * @param nextState Der neue State.
     */
    componentWillUpdate(nextProps: IProperties, nextState: IState)
    {
        this._isSaveButtonDisabled = true;

        if (this._initialState != null)
        {
            const { form } = this._initialState;

            if (form.userGroup.selectedValue !== nextState.form.userGroup.selectedValue)
                this._isSaveButtonDisabled = false;

            if (form.mortician.selectedValue !== nextState.form.mortician.selectedValue)
                this._isSaveButtonDisabled = false;

            if (form.email !== nextState.form.email)
                this._isSaveButtonDisabled = false;

            if (form.firstname !== nextState.form.firstname)
                this._isSaveButtonDisabled = false;

            if (form.lastname !== nextState.form.lastname)
                this._isSaveButtonDisabled = false;

            if (form.password !== nextState.form.password)
                this._isSaveButtonDisabled = false;

            if (form.passwordConfirmed !== nextState.form.passwordConfirmed)
                this._isSaveButtonDisabled = false;

            if (form.timezone.selectedValue !== nextState.form.timezone.selectedValue)
                this._isSaveButtonDisabled = false;

            if (form.isDeactivated !== nextState.form.isDeactivated)
                this._isSaveButtonDisabled = false;
        }
    }

    /**
     * Wird ausgeführt, bevor der Dialog sich schließt.
     */
    private onClosing = (refresh: boolean = false) =>
    {
        var closeDialog: boolean = true;

        if (!this._isSaveButtonDisabled)
            closeDialog = confirm("Es sind noch ungespeicherte Änderungen vorhanden. Wollen Sie den Dialog trotzdem schließen?");

        if (closeDialog)
        {
            const { onClose } = this.props;
            onClose && onClose(refresh);
            return true;
        }
    }

    /**
     * Ändert den geöffneten Datensatz oder erstellt einen neuen.
     */
    private onSaveOrCreateClick = async () =>
    {
        try
        {
            this.setState({
                isLoading: true
            });

            const { mode, user, form } = this.state;

            // Neues POST Objekt erstellen.
            var newData: modelsPost.IUser = {};
            newData.user_group_id = form.userGroup.selectedValue;
            newData.mortician_id = form.mortician.selectedValue;
            newData.email = form.email;
            newData.firstname = form.firstname;
            newData.lastname = form.lastname;
            newData.password = form.password;
            newData.password_confirmed = form.passwordConfirmed;
            newData.timezone = form.timezone.selectedValue;
            newData.is_deactivated = form.isDeactivated;

            if (mode === Mode.edit)
            {
                await apiUsers.put(user.id, newData);
            }
            else
            {
                await apiUsers.post(newData);
            }

            // Fenster schließen.
            this._isSaveButtonDisabled = true;
            this.onClosing(true);
        }
        catch (e)
        {
            var errors = utils.getErrors(e);
            var alertMessage: string;

            if (errors != null)
            {
                for (let error in errors)
                {
                    switch (error)
                    {
                        case "error":
                        case "date_is_invalid":
                            alertMessage = errors[error];
                            break;
                        case "internal_server_error":
                            alertMessage = "Auf dem Server ist ein Fehler aufgetreten! Wenn der Fehler weiterhin auftritt, kontaktieren Sie Ihren Administrator oder Ihren Software Anbieter.";
                            break;
                    }
                }
            }

            this.setState(() =>
            {
                const newState: IState = {};
                newState.isLoading = false;
                newState.errors = errors;
                newState.alertMessage = alertMessage;
                newState.alertType = AlertType.error;
                return newState;
            });
        }
    }

    /**
     * Löscht den geöffneten Datensatz.
     */
    private onDeleteClick = async () =>
    {
        var deleteData = confirm("Wollen Sie den Datensatz wirklich löschen?");
        if (!deleteData)
            return;

        try
        {
            this.setState({
                isLoading: true
            });

            const { user } = this.state;

            await apiUsers.remove(user.id);

            // Fenster schließen.
            this._isSaveButtonDisabled = true;
            this.onClosing(true);
        }
        catch (e)
        {
            var errors = utils.getErrors(e);
            var alertMessage: string;

            if (errors != null)
            {
                for (let error in errors)
                {
                    switch (error)
                    {
                        case "error":
                        case "date_is_invalid":
                            alertMessage = errors[error];
                            break;
                        case "internal_server_error":
                            alertMessage = "Auf dem Server ist ein Fehler aufgetreten! Wenn der Fehler weiterhin auftritt, kontaktieren Sie Ihren Administrator oder Ihren Software Anbieter.";
                            break;
                    }
                }
            }

            this.setState((state) =>
            {
                const newState: IState = {};
                newState.isLoading = false;
                newState.errors = errors;
                newState.alertMessage = alertMessage;
                newState.alertType = AlertType.error;
                return newState;
            });
        }
    }

    /**
     * Wird ausgeführt, sobald sich der Wert einer ComboBox ändert.
     * @param selectedItem Das neu selektierte Item.
     * @param valueName Der Property Name für den "value" Wert.
     * @param selectedValueName Der Property Name für den "selectedValue" Wert.
     * @param formItemName Der Name des Form Elements.
     */
    private onComboBoxChanged = (selectedItem: any, valueName: string, selectedValueName: string, formItemName: string) =>
    {
        this.setState((state: IState) =>
        {
            state.form[formItemName] = {
                value: selectedItem[valueName],
                selectedValue: selectedItem[selectedValueName]
            };
            return state;
        });
    }

    /**
     * Entfernt die Selektion einer ComboBox.
     * @param formItemName Der Name des Form Elements.
     */
    private clearComboBoxValue = (formItemName: string) =>
    {
        this.setState((state: IState) =>
        {
            state.form[formItemName] = {};
            return state;
        });
    }

    /**
     * Wird ausgeführt, sobald sich der Wert eines Controls ändert.
     * @param value Der konvertierte Wert der TextBox.
     * @param originalValue Der originale Wert der TextBox.
     * @param name Der Name des Form Elements.
     */
    private onControlTextChanged = (value: any[], originalValue: string[], name: string) =>
    {
        this.setState((state: IState) =>
        {
            state.form[name] = value[0] === "" ? undefined : value[0];
            return state;
        });
    }

    /**
     * Wird ausgeführt, sobald sich der Wert einer CheckBox ändert.
     * @param e Das auslösende Ereignis.
     */
    private onCheckboxChanged = (e: React.ChangeEvent<HTMLInputElement>) =>
    {
        var target = e.target;
        var value = target.checked;

        this.setState((state: IState) =>
        {
            state.form.isDeactivated = value;
            return state;
        });
    }

    /**
     * Rendert das Formular/den Content Bereich.
     */
    private renderContent(): JSX.Element
    {
        const { isLicenceLimitReached } = this.props;
        const {
            mode,
            user,
            userGroups,
            morticians,
            timezones,
            form,
            errors,
            alertMessage,
            alertType
        } = this.state;

        return (
            <div className="settings_dialog_content">
                <ScrollViewer
                    HorizontalScrollBarVisibility={ScrollBarVisibility.hidden}
                    VerticalScrollBarVisibility={ScrollBarVisibility.auto}
                    style={{
                        padding: 10
                    }}>
                    <FormGroup>
                        <ComboBox
                            label="Benutzergruppe"
                            dataSource={userGroups}
                            dropDownMenuItemHeight={DROPDOWN_TEXT_ITEM_HEIGHT}
                            comboBoxType={ComboBoxType.dropDownList}
                            value={form.userGroup.value}
                            selectedValue={form.userGroup.selectedValue}
                            onSelectionChanged={(data) => this.onComboBoxChanged(data, "name", "id", "userGroup")}
                            valueMember="id"
                            displayMember="name"
                            errorMessage={errors["UserGroupId"]}>
                            {
                                (item: modelsGet.IUserGroup) =>
                                {
                                    return (
                                        <DropDownTextItem
                                            key={item.id}
                                            value={item.id}
                                            displayValue={item.name} />
                                    );
                                }
                            }
                        </ComboBox>
                        <ComboBox
                            canRemoveSelection={true}
                            label="Bestatter"
                            dataSource={morticians}
                            dropDownMenuItemHeight={DROPDOWN_TEXT_ITEM_HEIGHT}
                            comboBoxType={ComboBoxType.dropDownList}
                            value={form.mortician.value}
                            selectedValue={form.mortician.selectedValue}
                            onSelectionChanged={(data) => this.onComboBoxChanged(data, "fullname_and_address_without_title_rev", "id", "mortician")}
                            valueMember="id"
                            displayMember="fullname_and_address_without_title_rev"
                            sideButtons={[
                                <InputSideButton
                                    icon="close"
                                    onClick={() => this.clearComboBoxValue("mortician")} />
                            ]}>
                            {
                                (item: modelsGet.IPerson) =>
                                {
                                    return (
                                        <DropDownTextItem
                                            key={item.id}
                                            value={item.id}
                                            displayValue={item.fullname_and_address_without_title_rev} />
                                    );
                                }
                            }
                        </ComboBox>
                        <TextBox
                            label="E-Mail"
                            value={form.email}
                            size={InputSize.normal}
                            autoFocus={true}
                            errorMessage={errors["Email"]}
                            onTextChange={(value, originalValue) => this.onControlTextChanged(value, originalValue, "email")} />
                        <TextBox
                            label="Vorname"
                            value={form.firstname}
                            size={InputSize.normal}
                            errorMessage={errors["Firstname"]}
                            onTextChange={(value, originalValue) => this.onControlTextChanged(value, originalValue, "firstname")} />
                        <TextBox
                            label="Nachname"
                            value={form.lastname}
                            size={InputSize.normal}
                            errorMessage={errors["Lastname"]}
                            onTextChange={(value, originalValue) => this.onControlTextChanged(value, originalValue, "lastname")} />
                        <TextBox
                            label="Passwort"
                            value={form.password}
                            size={InputSize.normal}
                            isPassword={true}
                            hint="Wählen Sie mindestens 8 Zeichen. Verwenden Sie kein Passwort von einer anderen Website und kein einfach zu erratendes Passwort wie den Namen Ihres Haustiers."
                            errorMessage={errors["Password"]}
                            onTextChange={(value, originalValue) => this.onControlTextChanged(value, originalValue, "password")} />
                        <TextBox
                            label="Passwort bestätigen"
                            value={form.passwordConfirmed}
                            size={InputSize.normal}
                            isPassword={true}
                            errorMessage={errors["PasswordConfirmed"]}
                            onTextChange={(value, originalValue) => this.onControlTextChanged(value, originalValue, "passwordConfirmed")} />
                        <ComboBox
                            label="Zeitzone"
                            dataSource={timezones}
                            dropDownMenuItemHeight={DROPDOWN_TEXT_ITEM_HEIGHT}
                            comboBoxType={ComboBoxType.dropDownList}
                            value={form.timezone.value}
                            selectedValue={form.timezone.selectedValue}
                            onSelectionChanged={(data) => this.onComboBoxChanged(data, "text", "key", "timezone")}
                            valueMember="key"
                            displayMember="text">
                            {
                                (item: modelsGet.ITimezone) =>
                                {
                                    return (
                                        <DropDownTextItem
                                            key={item.key}
                                            value={item.key}
                                            displayValue={item.text} />
                                    );
                                }
                            }
                        </ComboBox>
                        <CheckBox
                            style={{
                                marginTop: 10
                            }}
                            label="Deaktiviert"
                            isChecked={form.isDeactivated}
                            isDisabled={isLicenceLimitReached}
                            onCheckedChanged={this.onCheckboxChanged} />
                    </FormGroup>
                </ScrollViewer>
            </div>
        );
    }

    /**
     * Rendert diese Komponente.
     */
    public render(): JSX.Element
    {
        const {
            isLoading,
            mode,
            user,
            form,
            errors,
            alertMessage,
            alertType
        } = this.state;

        var icon: string = "loading";
        var title: string = "Daten werden geladen...";
        var buttonHeader: string = "";

        if (!isLoading)
        {
            if (mode === Mode.create)
            {
                icon = "plus";
                title = "Neuen Benutzer erstellen";
                buttonHeader = "Erstellen";
            }
            else
            {
                icon = "pencil";
                title = `Benutzer "${user.fullname}" bearbeiten`;
                buttonHeader = "Speichern";
            }
        }

        return (
            <Window
                isOpen={true}
                titleIcon={icon}
                title={title}
                onClosing={this.onClosing}
                isMaximized={false}
                width={600}
                height={700}
                onEnterKeyUp={this.onSaveOrCreateClick}
                onEscapeKeyUp={() => this.onClosing(false)}>
                <div className="settings_dialog">
                    <Alert
                        style={utils.getGridChildStyle(1, 1, 1, 1)}
                        alertType={alertType}
                        isVisible={alertMessage ? true : false}
                        isClosable={true}
                        onHide={() => this.setState({ alertMessage: null, alertType: null })}>
                        {alertMessage}
                    </Alert>
                    {isLoading ? <Loader loaderStyle={LoaderStyle.style1} /> : this.renderContent()}
                    <div className="settings_dialog_actions">
                        {
                            mode === Mode.edit &&
                            <Button
                                text="Löschen"
                                type={ButtonType.danger}
                                onClick={this.onDeleteClick} />
                        }
                        <div style={{ flexGrow: 1 }}></div>
                        <Button
                            text={buttonHeader}
                            type={ButtonType.primary}
                            isDisabled={this._isSaveButtonDisabled}
                            onClick={this.onSaveOrCreateClick} />
                        <Button
                            text="Abbrechen"
                            type={ButtonType.default}
                            onClick={() => this.onClosing(false)} />
                    </div>
                </div>
            </Window>
        );
    }
}